import * as React from 'react'
import styled from 'styled-components'

import { Picture } from './Picture'

import * as S from '../styles'

export type GalleryProps = {
    focused?: number
    initialFocused?: number
    list: {
        alt?: string
        height?: number
        nativeLazyLoad?: boolean
        src: string
        srcset?: string
        width?: number
    }[]
    onItemFocus?: (index: number) => void
} & React.HTMLAttributes<HTMLDivElement>

export function GalleryComponent(props: GalleryProps): React.ReactElement {
    React.useEffect(() => {
        if (process.env.NODE_ENV === 'production') {
            return
        }

        if (props.focused !== undefined && props.initialFocused !== undefined) {
            console.error(
                new Error(
                    '<Gallery> is both controlled and uncontrolled, use either focused or initialFocused'
                )
            )
        }
    }, [])

    if (props.focused !== undefined) {
        return <ControlledGalleryComponent {...props} />
    } else return <UncontrolledGalleryComponent {...props} />
}

export const Gallery = styled(GalleryComponent)``

function UncontrolledGalleryComponent({
    initialFocused = 0,
    list,
    onItemFocus,
    ...divProps
}: GalleryProps): React.ReactElement {
    const [focused, setFocused] = React.useState(initialFocused)

    return (
        <ControlledGalleryComponent
            {...divProps}
            focused={focused}
            list={list}
            onItemFocus={index => {
                setFocused(index)
                onItemFocus && onItemFocus(index)
            }}
        />
    )
}

function ControlledGalleryComponent({
    list,
    focused = 0,
    onItemFocus = () => {
        /* noop */
    },
    ...divProps
}: GalleryProps): React.ReactElement {
    const highest = list.reduce((highest, image) => {
        if (Number(highest.height) < Number(image.height)) {
            return image
        }
        return highest
    }, list[0])

    return (
        <S.gallery.Wrapper {...divProps}>
            <Picture
                hasZoom={true}
                sizing="contain"
                {...list[focused]}
                sizeof={highest.src}
            />
            {list.length > 1 && (
                <S.gallery.List>
                    {list.map((img, i) => (
                        <S.gallery.Li key={img.src} onClick={() => onItemFocus(i)}>
                            <S.gallery.Thumbnail>
                                <Picture sizing="contain" {...img} />
                            </S.gallery.Thumbnail>
                        </S.gallery.Li>
                    ))}
                </S.gallery.List>
            )}
        </S.gallery.Wrapper>
    )
}

type GallerySkeletonProps = {
    className?: string
}

function GallerySkeletonComponent({ className }: GallerySkeletonProps) {
    return (
        <S.gallery.Wrapper className={className}>
            <S.skeleton.Rectangle height={500} />
            <S.gallery.List>
                {[...new Array(5).keys()].map(item => (
                    <S.gallery.Li key={item}>
                        <S.gallery.Thumbnail>
                            <S.skeleton.Rectangle height={150} />
                        </S.gallery.Thumbnail>
                    </S.gallery.Li>
                ))}
            </S.gallery.List>
        </S.gallery.Wrapper>
    )
}

export const GallerySkeleton = styled(GallerySkeletonComponent)``
